import { UseQueryResult } from '@tanstack/react-query';
import React from 'react';
import { Spinner } from '../../components';
import { ViewError, ViewLoader } from './components';

interface Props<TData extends readonly unknown[] = readonly unknown[], TError = unknown> {
  queries: readonly UseQueryResult<TData[number], TError>[];
  children: (data: TData) => React.ReactElement;
  loader?: React.ReactNode;
  inline?: boolean;
  ignore404?: boolean;
  noLoader?: boolean;
}

export function QueriesView<
  TData extends readonly unknown[] = readonly unknown[],
  TError = unknown,
>({ queries, children, loader, inline, ignore404, noLoader }: Props<TData, TError>) {
  const isLoading = queries.some((query) => query.isLoading);
  const hasError = queries.some(
    (query) =>
      query.error && !(ignore404 && (query.error as any)?.axiosError?.response?.status === 404),
  );
  const error = queries.find(
    (query) =>
      query.error && !(ignore404 && (query.error as any)?.axiosError?.response?.status === 404),
  )?.error;
  const allDataDefined = queries.every((query) => typeof query.data !== 'undefined');

  if (isLoading) {
    if (noLoader) {
      return null;
    }

    return <>{loader ?? (inline ? <Spinner /> : <ViewLoader />)}</>;
  }

  if (hasError && error) {
    return <ViewError error={error as unknown as Error} />;
  }

  if (allDataDefined || ignore404) {
    const data = queries.map((query) => query.data) as unknown as TData;
    return children(data);
  }

  return null;
}
